import { useEffect, useRef } from 'react';

import { InviteLeaseDetailsSegmentEvents } from '../constants';
import { useInviteTracking } from './use-invite-tracking';

type LabelsTracked = {
  propertyId: string | number;
  error: boolean;
  success: boolean;
};

type AxiosErrorShape = {
  name?: string;
  response?: { status?: number };
  code?: string;
};

type UseTrackScreeningLabelsLoadedProps = {
  propertyId: string | number | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasCustomScreeningStatusLabels: boolean;
  labelCount: number;
  enabled?: boolean;
};

// Fires `LabelsResolved/Loaded` once per property per outcome; re-fires only when the outcome flips.
export const useTrackScreeningLabelsLoaded = ({
  propertyId,
  isLoading,
  isError,
  error,
  hasCustomScreeningStatusLabels,
  labelCount,
  enabled = true,
}: UseTrackScreeningLabelsLoadedProps) => {
  const { trackInvite } = useInviteTracking();
  const labelsTrackedRef = useRef<LabelsTracked | null>(null);

  useEffect(() => {
    if (!propertyId || isLoading || !enabled) return;

    const tracked = labelsTrackedRef.current;
    const isSameProperty = tracked?.propertyId === propertyId;

    if (isError) {
      if (isSameProperty && tracked?.error) return;
      labelsTrackedRef.current = {
        propertyId,
        error: true,
        success: (isSameProperty && tracked?.success) || false,
      };
      const axiosError = error as AxiosErrorShape | null | undefined;
      trackInvite(InviteLeaseDetailsSegmentEvents.LabelsResolved, {
        property_id: propertyId,
        resolved_successfully: false,
        has_custom_screening_status_labels: hasCustomScreeningStatusLabels,
        label_count: 0,
        error_type: axiosError?.name ?? 'unknown',
        http_status: axiosError?.response?.status,
        error_code: axiosError?.code,
      });
      return;
    }

    if (isSameProperty && tracked?.success) return;
    labelsTrackedRef.current = {
      propertyId,
      error: (isSameProperty && tracked?.error) || false,
      success: true,
    };
    trackInvite(InviteLeaseDetailsSegmentEvents.LabelsResolved, {
      property_id: propertyId,
      resolved_successfully: true,
      has_custom_screening_status_labels: hasCustomScreeningStatusLabels,
      label_count: labelCount,
    });
  }, [
    propertyId,
    isLoading,
    isError,
    error,
    hasCustomScreeningStatusLabels,
    labelCount,
    enabled,
    trackInvite,
  ]);
};
